import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { usePuzzle } from './usePuzzle'
import type { PuzzleDifficulty, PuzzleMode } from './usePuzzle'
import {
  getPieceStyle,
  getDiagonalOverlayGradient,
} from './puzzleUtils'
import type { PieceDef, PuzzleAlt, PairsCard as PairsCardType } from './puzzleUtils'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import GamePlayHeader from '../GamePlayHeader'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useSessionMode } from '../../professional/SessionModeContext'
import { themes } from '../../api/client'
import type { ThemeSummary } from '../../api/client'
import { toGameItemsFromTheme, formatGameTime } from '../utils'
import { useTimer } from '../useTimer'
import { COLORS, SHADOWS, RADIUS, FONT } from '../../styles/tokens'
import type { GameItem } from '../../i18n'

type GameScreen = 'setup' | 'playing' | 'complete'
const MODES: PuzzleMode[]       = ['puzzle', 'pairs']
const DIFFICULTIES: PuzzleDifficulty[] = ['easy', 'medium', 'hard']
const OVERLAY_BG = COLORS.bgPage

/** Find the column count that maximises card width for portrait (1:2) cards. */
function computePairsGrid(N: number, areaW: number, areaH: number, gap: number) {
  let bestCols = 1
  let bestW    = 0
  for (let c = 1; c <= N; c++) {
    const r      = Math.ceil(N / c)
    const fromW  = (areaW - gap * (c - 1)) / c
    const fromH  = (areaH - gap * (r - 1)) / (r * 2) // card height = 2 × width
    const w      = Math.min(fromW, fromH)
    if (w > bestW) { bestW = w; bestCols = c }
  }
  return { cols: bestCols, cardW: Math.floor(bestW) }
}

/* ── Root ─────────────────────────────────────────────────────── */
export default function PuzzleGame({ onBack }: { onBack: () => void }) {
  const { t, language } = useTranslation()
  const sessionMode = useSessionMode()
  const c = t.puzzle

  const [screen,         setScreen]         = useState<GameScreen>(sessionMode ? 'playing' : 'setup')
  const [mode,           setMode]           = useState<PuzzleMode>((sessionMode?.config.mode as PuzzleMode) ?? 'puzzle')
  const [difficulty,     setDifficulty]     = useState<PuzzleDifficulty>((sessionMode?.config.difficulty as PuzzleDifficulty) ?? 'easy')
  const [availableThemes,setAvailableThemes]= useState<ThemeSummary[]>([])
  const [selectedTheme,  setSelectedTheme]  = useState<ThemeSummary | null>(
    (sessionMode?.config.theme as ThemeSummary | null | undefined) ?? null
  )
  const [images,         setImages]         = useState<GameItem[]>([])
  const [loadingTheme,   setLoadingTheme]   = useState(false)

  useEffect(() => {
    themes.list().then(setAvailableThemes).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    Promise.resolve()
      .then(() => {
        if (!selectedTheme) { if (!cancelled) setImages([]); return null }
        if (!cancelled) setLoadingTheme(true)
        return themes.get(selectedTheme.slug)
      })
      .then(theme => {
        if (cancelled || !theme) return
        setImages(toGameItemsFromTheme(theme.images, language))
        setLoadingTheme(false)
      })
      .catch(() => { if (!cancelled) setLoadingTheme(false) })
    return () => { cancelled = true }
  }, [selectedTheme, language])

  const hook = usePuzzle(difficulty, mode, images)
  const { state } = hook

  const pairsTotal   = mode === 'puzzle'
    ? state.rounds.length
    : state.cards.length / 2

  const pairsMatched = mode === 'puzzle'
    ? Math.min(state.currentRoundIdx, state.rounds.length)
    : state.matchedImageIds.size

  const { sessionId } = useGameSession({
    activitySlug: 'puzzle',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  state.gameComplete,
    activitySlug:  'puzzle',
    difficulty,
    mode,
    moves:         state.moves,
    startTime:     state.startTime,
    endTime:       state.endTime,
    pairsTotal,
    pairsMatched,
    themeId:       selectedTheme?.id,
    sessionId,
    onCompleted:   () => setScreen('complete'),
  })

  const handleStart = () => {
    hook.resetGame()
    setScreen('playing')
  }

  if (sessionMode && selectedTheme && loadingTheme) {
    return <div style={s.loadRoot}>…</div>
  }

  return (
    <div style={s.root}>
      {saveError && (
        <SaveErrorBanner
          message={t.common.saveError}
          dismissLabel={t.common.dismissError}
          onDismiss={clearSaveError}
        />
      )}

      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          onModeChange={setMode}
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          availableThemes={availableThemes}
          selectedTheme={selectedTheme}
          onThemeChange={setSelectedTheme}
          loadingTheme={loadingTheme}
          hasEnoughImages={images.length >= 2}
          onBack={onBack}
          onStart={handleStart}
        />
      )}

      {screen === 'playing' && (
        <PlayScreen
          hook={hook}
          mode={mode}
          onBack={sessionMode ? onBack : () => setScreen('setup')}
        />
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🧩"
          title={c.complete.title}
          subtitle={c.complete.subtitle}
          stats={[
            { value: String(state.moves),                          label: c.complete.movesLabel },
            { value: formatGameTime(state.startTime, state.endTime), label: c.complete.timeLabel },
            { value: c.difficulty[difficulty].label,               label: c.complete.levelLabel },
          ]}
          playAgainLabel={c.complete.playAgain}
          changeLevelLabel={c.complete.changeLevel}
          backLabel={c.complete.backToDashboard}
          onPlayAgain={handleStart}
          onChangeLevel={() => setScreen('setup')}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ─────────────────────────────────────────────── */
function SetupScreen({
  mode, onModeChange, difficulty, onDifficultyChange,
  availableThemes, selectedTheme, onThemeChange, loadingTheme,
  hasEnoughImages, onBack, onStart,
}: {
  mode:               PuzzleMode
  onModeChange:       (m: PuzzleMode) => void
  difficulty:         PuzzleDifficulty
  onDifficultyChange: (d: PuzzleDifficulty) => void
  availableThemes:    ThemeSummary[]
  selectedTheme:      ThemeSummary | null
  onThemeChange:      (t: ThemeSummary) => void
  loadingTheme:       boolean
  hasEnoughImages:    boolean
  onBack:             () => void
  onStart:            () => void
}) {
  const { t, language } = useTranslation()
  const c = t.puzzle
  const canStart = selectedTheme !== null && hasEnoughImages && !loadingTheme

  return (
    <GameSetupScreen
      title={c.title}
      subtitle={c.subtitle}
      backLabel={c.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map(id => ({ id, ...c.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={id => onDifficultyChange(id as PuzzleDifficulty)}
      chooseDifficultyLabel={c.chooseDifficultyTitle}
      startLabel={c.startBtn}
      onStart={onStart}
      themeLoading={loadingTheme}
      startDisabled={!canStart}
    >
      {/* Theme picker */}
      <section>
        <h2 style={ss.optionLabel}>{c.chooseThemeTitle}</h2>
        <div style={ss.themeRow}>
          {availableThemes.length === 0 && (
            <p style={s.loadingText}>…</p>
          )}
          {availableThemes.map(theme => {
            const isActive = selectedTheme?.slug === theme.slug
            return (
              <button
                key={theme.slug}
                onClick={() => onThemeChange(theme)}
                style={{ ...ss.themeBtn, ...(isActive ? ss.themeBtnActive : {}) }}
                aria-pressed={isActive}
              >
                {theme.coverUrl
                  ? <img src={theme.coverUrl} alt="" style={ss.themeCover} />
                  : <span style={ss.themeEmoji}>🖼️</span>
                }
                <span style={ss.themeLabel}>
                  {language === 'es' ? theme.nameEs : theme.nameEn}
                </span>
              </button>
            )
          })}
        </div>
        {!selectedTheme && availableThemes.length > 0 && (
          <p style={s.themeHint}>{c.chooseThemeHint}</p>
        )}
      </section>

      {/* Mode picker */}
      <section>
        <h2 style={ss.optionLabel}>{c.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map(m => (
            <button
              key={m}
              onClick={() => onModeChange(m)}
              style={{ ...ss.modeCard, ...(mode === m ? ss.modeCardActive : {}) }}
              aria-pressed={mode === m}
            >
              <span style={s.modeEmoji}>{m === 'puzzle' ? '🧩' : '✂️'}</span>
              <span style={s.modeLabel}>{c.modes[m].label}</span>
              <span style={s.modeDesc}>{c.modes[m].description}</span>
            </button>
          ))}
        </div>
      </section>
    </GameSetupScreen>
  )
}

/* ── Play Screen (both modes) ─────────────────────────────────── */
function PlayScreen({
  hook, mode, onBack,
}: {
  hook:   ReturnType<typeof usePuzzle>
  mode:   PuzzleMode
  onBack: () => void
}) {
  const { t } = useTranslation()
  const c = t.puzzle
  const { state } = hook
  const [isLandscape, setIsLandscape] = useState(
    () => typeof window !== 'undefined' && window.innerWidth > window.innerHeight,
  )
  useEffect(() => {
    const h = () => setIsLandscape(window.innerWidth > window.innerHeight)
    window.addEventListener('resize', h, { passive: true })
    return () => window.removeEventListener('resize', h)
  }, [])

  const timer = useTimer(state.startTime, state.endTime)
  /* Portrait puzzle: measure board+alts area to size them precisely */
  const puzzleBoardAreaRef = useRef<HTMLDivElement>(null)
  const [boardArea, setBoardArea] = useState({ w: 0, h: 0 })
  useEffect(() => {
    if (isLandscape || mode !== 'puzzle') return
    const el = puzzleBoardAreaRef.current
    if (!el) return
    const measure = () => setBoardArea({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isLandscape, mode])

  /* Pairs: measure available area and compute optimal grid */
  const boardWrapRef = useRef<HTMLDivElement>(null)
  const [pairsGrid, setPairsGrid] = useState({ cols: 3, cardW: 80 })
  useEffect(() => {
    if (mode !== 'pairs') return
    const el = boardWrapRef.current
    if (!el) return
    const N   = state.cards.length
    const GAP = 8
    const PAD = 10
    const compute = () => {
      const w = el.clientWidth  - PAD * 2
      const h = el.clientHeight - PAD * 2
      if (w > 0 && h > 0) setPairsGrid(computePairsGrid(N, w, h, GAP))
    }
    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mode, state.cards.length])

  /* ── Puzzle mode ── */
  if (mode === 'puzzle') {
    const roundIdx = Math.min(state.currentRoundIdx, state.rounds.length - 1)
    const round    = state.rounds[roundIdx]
    if (!round) return null

    const totalRounds = state.rounds.length
    const progress    = (state.currentRoundIdx / Math.max(1, totalRounds)) * 100
    const boardAspect = '1 / 1'
    const gr = round.cutDef.gridRows
    // Landscape board: 40dvh capped at 340px (matches CSS min())
    const lsBoardPx = typeof window !== 'undefined'
      ? Math.floor(Math.min(window.innerHeight * 0.4, 340))
      : 300
    const lsAltH = Math.floor(lsBoardPx / gr)

    return (
      <div style={s.playRoot}>
        <GamePlayHeader
          title={c.title}
          subtitle={`${c.round} ${Math.min(state.currentRoundIdx + 1, totalRounds)}/${totalRounds}`}
          stats={[
            { value: String(state.moves), label: c.moves },
            { value: timer,               label: c.time  },
          ]}
          progress={progress}
          progressNow={state.currentRoundIdx}
          progressMax={totalRounds}
          onBack={onBack}
          backLabel={c.backToDashboard}
        />

        {isLandscape ? (
          /* ── Landscape: board + alts sized to exact piece dimensions ── */
          <div style={{
            ...s.playContent,
            flexDirection:  'row',
            overflow:       'hidden',
            alignItems:     'center',
            justifyContent: 'center',
            flexWrap:       'nowrap',
          }}>
            <div style={{ ...s.boardSection, flexShrink: 0 }}>
              <p style={s.instruction}>{c.instructionPick}</p>
              {round.cutDef.type === 'diagonal' ? (
                <DiagonalBoard
                  imageUrl={round.imageItem.imageUrl ?? ''}
                  anchorPiece={round.anchorPieces[0]}
                  missingPiece={round.missingPiece}
                  boardWidth={`${lsBoardPx}px`}
                />
              ) : (
                <div style={{
                  display:             'grid',
                  gridTemplateColumns: `repeat(${round.cutDef.gridCols}, 1fr)`,
                  gridTemplateRows:    `repeat(${round.cutDef.gridRows}, 1fr)`,
                  width:               lsBoardPx,
                  height:              lsBoardPx,
                  flexShrink:          0,
                  gap:                 3,
                  backgroundColor:     COLORS.greenDark,
                  border:              `3px solid ${COLORS.greenDark}`,
                  borderRadius:        RADIUS.md,
                  overflow:            'hidden',
                  boxShadow:           SHADOWS.medium,
                }}>
                  {round.cutDef.pieces.map(piece => (
                    <PieceCell
                      key={piece.id}
                      piece={piece}
                      imageUrl={round.imageItem.imageUrl ?? ''}
                      isMissing={piece.id === round.missingPiece.id}
                    />
                  ))}
                </div>
              )}
            </div>
            {/* Alts: rows = gridRows, each row height = board/gridRows (matches piece) */}
            <div style={{
              height:           lsBoardPx,
              display:          'grid',
              gridTemplateRows: `repeat(${gr}, ${lsAltH}px)`,
              gridAutoFlow:     'column',
              overflowX:        'auto',
              overflowY:        'hidden',
              gap:              '0.5rem',
              padding:          '0 0.5rem',
            }}>
              {round.alternatives.map(alt => (
                <AltTile
                  key={alt.id}
                  alt={alt}
                  isCorrect={alt.id === state.correctFlashId}
                  isWrong={alt.id === state.wrongId}
                  fillDimension="height"
                  onSelect={() => hook.selectAlternative(alt.id)}
                  disabled={state.gameComplete}
                />
              ))}
            </div>
          </div>
        ) : (
          /* ── Portrait: instruction above, then measured area for board + alts ── */
          <div style={{
            display:       'flex',
            flex:          1,
            flexDirection: 'column',
            overflow:      'hidden',
            padding:       '0.5rem',
            gap:           '0.5rem',
            minHeight:     0,
          }}>
            <p style={{ ...s.instruction, flexShrink: 0 }}>{c.instructionPick}</p>
            {/* Measured area: board + alts sized together to fill remaining space */}
            <div
              ref={puzzleBoardAreaRef}
              style={{
                flex:          1,
                display:       'flex',
                flexDirection: 'column',
                overflow:      'hidden',
                gap:           '0.5rem',
                minHeight:     0,
                alignItems:    'center',
              }}
            >
              {(() => {
                const gr = round.cutDef.gridRows
                const GAP = 8
                // boardSize: fits both board and alts row (alts height = board/gridRows)
                const bSize = (boardArea.h > 0 && boardArea.w > 0)
                  ? Math.floor(Math.min(boardArea.w, (boardArea.h - GAP) * gr / (gr + 1)))
                  : 0
                const altH = bSize > 0 ? Math.floor(bSize / gr) : 0
                const bPx  = bSize > 0 ? bSize : undefined
                return (
                  <>
                    {round.cutDef.type === 'diagonal' ? (
                      <DiagonalBoard
                        imageUrl={round.imageItem.imageUrl ?? ''}
                        anchorPiece={round.anchorPieces[0]}
                        missingPiece={round.missingPiece}
                        boardWidth={bPx ? `${bPx}px` : '100%'}
                      />
                    ) : (
                      <div style={{
                        display:             'grid',
                        gridTemplateColumns: `repeat(${round.cutDef.gridCols}, 1fr)`,
                        gridTemplateRows:    `repeat(${round.cutDef.gridRows}, 1fr)`,
                        width:               bPx ?? '100%',
                        aspectRatio:         boardAspect,
                        flexShrink:          0,
                        gap:                 3,
                        backgroundColor:     COLORS.greenDark,
                        border:              `3px solid ${COLORS.greenDark}`,
                        borderRadius:        RADIUS.md,
                        overflow:            'hidden',
                        boxShadow:           SHADOWS.medium,
                      }}>
                        {round.cutDef.pieces.map(piece => (
                          <PieceCell
                            key={piece.id}
                            piece={piece}
                            imageUrl={round.imageItem.imageUrl ?? ''}
                            isMissing={piece.id === round.missingPiece.id}
                          />
                        ))}
                      </div>
                    )}
                    {/* Alts: one row at exact missing-piece height, scroll horizontally */}
                    <div style={{
                      flexShrink:       0,
                      alignSelf:        'stretch',
                      height:           altH > 0 ? altH : undefined,
                      flex:             altH > 0 ? undefined : 1,
                      minHeight:        0,
                      display:          'grid',
                      gridTemplateRows: '1fr',
                      gridAutoFlow:     'column',
                      overflowX:        'auto',
                      overflowY:        'hidden',
                      gap:              '0.5rem',
                    }}>
                      {round.alternatives.map(alt => (
                        <AltTile
                          key={alt.id}
                          alt={alt}
                          isCorrect={alt.id === state.correctFlashId}
                          isWrong={alt.id === state.wrongId}
                          fillDimension="height"
                          onSelect={() => hook.selectAlternative(alt.id)}
                          disabled={state.gameComplete}
                        />
                      ))}
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        )}
      </div>
    )
  }

  /* ── Pairs mode ── */
  const totalPairs = state.cards.length / 2
  const progress   = (state.matchedImageIds.size / Math.max(1, totalPairs)) * 100

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={c.title}
        subtitle={`${c.pairs} ${state.matchedImageIds.size}/${totalPairs}`}
        stats={[
          { value: String(state.moves), label: c.moves },
          { value: timer,               label: c.time  },
        ]}
        progress={progress}
        progressNow={state.matchedImageIds.size}
        progressMax={totalPairs}
        onBack={onBack}
        backLabel={c.backToDashboard}
      />
      <p style={{ ...s.instruction, margin: '0.4rem auto 0' }}>{c.instructionPairs}</p>
      <div ref={boardWrapRef} style={s.pairsWrap}>
        <div style={{
          display:             'grid',
          gridTemplateColumns: `repeat(${pairsGrid.cols}, ${pairsGrid.cardW}px)`,
          gap:                 8,
        }}>
          {state.cards.map(card => (
            <PairsCardTile
              key={card.id}
              card={card}
              cardSize={pairsGrid.cardW}
              isSelected={state.selectedIds.includes(card.id)}
              isMatched={state.matchedImageIds.has(card.imageId)}
              isWrong={(state.wrongPairIds ?? []).includes(card.id)}
              onSelect={() => hook.selectCard(card.id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── Piece cell (board piece or missing slot) ─────────────────── */
function PieceCell({
  piece, imageUrl, isMissing,
}: {
  piece:    PieceDef
  imageUrl: string
  isMissing:boolean
}) {
  if (isMissing) {
    return (
      <div style={{
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        backgroundColor: COLORS.bgCard,
        fontSize:        FONT.size.xl,
        color:           COLORS.textMuted,
        border:          `3px dashed ${COLORS.greenPale}`,
      }}>
        ?
      </div>
    )
  }
  return (
    <div style={{ position: 'relative', ...getPieceStyle(piece, imageUrl) }}>
      {piece.diagonalOverlay && (
        <div style={{
          position:       'absolute',
          inset:          0,
          background:     getDiagonalOverlayGradient(piece.diagonalOverlay, OVERLAY_BG),
          pointerEvents:  'none',
        }} />
      )}
    </div>
  )
}

/* ── Diagonal board (single square, full image + overlay) ───── */
function DiagonalBoard({
  imageUrl, anchorPiece, missingPiece, boardWidth,
}: {
  imageUrl:    string
  anchorPiece: PieceDef
  missingPiece:PieceDef
  boardWidth:  string
}) {
  const isMissingTopRight = missingPiece.diagonalOverlay === 'piece1'
  // Anchor overlay hides the missing triangle; hairline marks the cut
  const anchorOverlay = anchorPiece.diagonalOverlay!
  const direction = anchorOverlay === 'piece0' ? 'to top right' : 'to bottom left'
  const combinedGradient =
    `linear-gradient(${direction}, transparent calc(50% - 1.5px), ${COLORS.greenDark} calc(50% - 1.5px), ${COLORS.greenDark} 50%, ${OVERLAY_BG} 50%)`

  return (
    <div style={{
      width:           boardWidth,
      aspectRatio:     '1 / 1',
      position:        'relative',
      borderRadius:    RADIUS.md,
      overflow:        'hidden',
      boxShadow:       SHADOWS.medium,
      backgroundImage: `url(${imageUrl})`,
      backgroundSize:  '100% 100%',
      backgroundRepeat:'no-repeat',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: combinedGradient, pointerEvents: 'none' }} />
      <div style={{
        position:       'absolute',
        inset:          0,
        display:        'flex',
        alignItems:     isMissingTopRight ? 'flex-start' : 'flex-end',
        justifyContent: isMissingTopRight ? 'flex-end'   : 'flex-start',
        padding:        '12%',
        fontSize:       FONT.size.xl,
        color:          COLORS.textMuted,
      }}>
        ?
      </div>
    </div>
  )
}

/* ── Alternative tile (puzzle mode) ──────────────────────────── */
function AltTile({
  alt, isCorrect, isWrong, fillDimension, onSelect, disabled,
}: {
  alt:           PuzzleAlt
  isCorrect:     boolean
  isWrong:       boolean
  fillDimension: 'width' | 'height'
  onSelect:      () => void
  disabled:      boolean
}) {
  const { piece } = alt
  const borderColor = isCorrect ? COLORS.greenLight : isWrong ? COLORS.errorText : COLORS.borderWarm
  const btnStyle: React.CSSProperties = {
    position:        'absolute',
    inset:           0,
    border:          `3px solid ${borderColor}`,
    borderRadius:    RADIUS.md,
    overflow:        'hidden',
    cursor:          disabled ? 'default' : 'pointer',
    transition:      'border-color 0.15s, transform 0.1s',
    backgroundColor: COLORS.bgCard,
    boxShadow:       isCorrect ? SHADOWS.active : SHADOWS.subtle,
    ...getPieceStyle(piece, alt.imageItem.imageUrl ?? ''),
  }
  const overlay = piece.diagonalOverlay && (
    <div style={{
      position:      'absolute',
      inset:         0,
      background:    getDiagonalOverlayGradient(piece.diagonalOverlay, OVERLAY_BG),
      pointerEvents: 'none',
    }} />
  )

  const btn = (
    <button
      onClick={onSelect}
      disabled={disabled || isCorrect}
      className={isWrong ? 'wm-shake' : ''}
      aria-label={alt.imageItem.word}
      style={btnStyle}
    >
      {overlay}
    </button>
  )

  if (fillDimension === 'height') {
    // Landscape: grid row height is a known 1fr — aspect-ratio sets the width
    return (
      <div style={{ height: '100%', aspectRatio: `${piece.gridRows} / ${piece.gridCols}`, position: 'relative' }}>
        {btn}
      </div>
    )
  }

  // Portrait: padding-bottom trick → reliable cross-browser aspect ratio
  // Board is square; piece is W/gridCols wide × W/gridRows tall.
  // paddingBottom = height/width × 100 = (gridCols/gridRows) × 100
  const pct = `${(piece.gridCols / piece.gridRows) * 100}%`
  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: pct }}>
      {btn}
    </div>
  )
}

/* ── Pairs card tile ──────────────────────────────────────────── */
function PairsCardTile({
  card, cardSize, isSelected, isMatched, isWrong, onSelect,
}: {
  card:       PairsCardType
  cardSize:   number
  isSelected: boolean
  isMatched:  boolean
  isWrong:    boolean
  onSelect:   () => void
}) {
  const cardH = Math.round(cardSize * card.piece.gridCols / card.piece.gridRows)
  return (
    <button
      onClick={onSelect}
      disabled={isMatched}
      className={isWrong ? 'wm-shake' : ''}
      aria-label={card.imageItem.word}
      aria-pressed={isSelected}
      style={{
        width:           cardSize,
        height:          cardH,
        position:        'relative',
        border:          `3px solid ${
          isSelected ? COLORS.green :
          isMatched  ? COLORS.greenPale :
          isWrong    ? COLORS.errorText :
          COLORS.borderWarm
        }`,
        borderRadius:    RADIUS.md,
        overflow:        'hidden',
        cursor:          isMatched ? 'default' : 'pointer',
        opacity:         isMatched ? 0.35 : 1,
        transition:      'border-color 0.15s, opacity 0.35s',
        flexShrink:      0,
        backgroundColor: COLORS.bgCard,
        boxShadow:       isSelected ? SHADOWS.active : SHADOWS.subtle,
        ...getPieceStyle(card.piece, card.imageItem.imageUrl ?? ''),
      }}
    >
      {card.piece.diagonalOverlay && (
        <div style={{
          position:      'absolute',
          inset:         0,
          background:    getDiagonalOverlayGradient(card.piece.diagonalOverlay, OVERLAY_BG),
          pointerEvents: 'none',
        }} />
      )}
    </button>
  )
}

/* ── Styles ───────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:       '100dvh',
    backgroundColor: COLORS.bgPage,
  },
  loadRoot: {
    minHeight:       '100dvh',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    fontSize:        FONT.size.xl,
    backgroundColor: COLORS.bgPage,
    color:           COLORS.textSecondary,
  },
  loadingText: {
    fontSize: FONT.size.base,
    color:    COLORS.textSecondary,
    margin:   0,
  },
  themeHint: {
    fontSize:   FONT.size.sm,
    color:      COLORS.textSecondary,
    marginTop:  '0.5rem',
    fontStyle:  'italic',
  },
  modeEmoji: {
    fontSize: FONT.size.xl,
  },
  modeLabel: {
    fontSize:   FONT.size.base,
    fontWeight: 600,
    color:      COLORS.textPrimary,
  },
  modeDesc: {
    fontSize:  FONT.size.sm,
    color:     COLORS.textSecondary,
    textAlign: 'center' as const,
  },
  playRoot: {
    height:          '100dvh',
    display:         'flex',
    flexDirection:   'column',
    overflow:        'hidden' as const,
    backgroundColor: COLORS.bgPage,
  },
  playContent: {
    display:        'flex',
    flex:           1,
    gap:            '0.75rem',
    padding:        '0.75rem',
    alignItems:     'center',
    justifyContent: 'center',
    flexWrap:       'wrap' as const,
  },
  boardSection: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            '0.5rem',
  },
  altsSection: {
    display:        'flex',
    gap:            '0.5rem',
    alignItems:     'center',
    justifyContent: 'center',
  },
  pairsWrap: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        10,
    overflow:       'hidden' as const,
  },
  instruction: {
    fontSize:   FONT.size.md,
    color:      COLORS.textSecondary,
    fontStyle:  'italic',
    margin:     0,
    textAlign:  'center' as const,
    maxWidth:   '36ch',
  },
}
